import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { InfoBlock, ListTable, orDash } from './DetailParts';
import { STATUS_COLORS, formatDuration, formatTime, formatUnix, yesNo } from './updateSummary';

// What the reboot watch calls a restart reason, in plain English.
const REBOOT_REASONS = {
  ComponentBasedServicing: 'Windows servicing',
  WindowsUpdate: 'Windows Update',
  PendingComputerRename: 'Computer rename',
  ConfigMgrClient: 'Configuration Manager',
};
const REBOOT_PHASES = {
  daily: 'Daily reminders',
  lastday: 'Last day (reminders every few hours)',
  final: 'Final warning (cannot be dismissed)',
};
const REBOOT_STATUS_TEXT = {
  waiting: 'Restart pending - waiting for the scheduled restart',
  notified: 'Restart pending - the user was just notified',
  restarting: 'Restarting now',
  'no-reboot-required': 'The reboot watch sees no restart pending',
};

// When a pending restart will happen and how long it has been pending, from the
// invoke-rebootwatch result (the reboot watch keeps its own deadline for each device).
// `reboot` is { state: 'loading' | 'unscheduled' | 'none' | 'error' | 'ok', item?, error? }.
export function RebootInfo({ reboot }) {
  if (!reboot) return null;
  const boxSx = { p: 1.5, borderColor: 'warning.main' };
  if (reboot.state === 'loading') {
    return (
      <Paper variant="outlined" sx={boxSx}>
        <Typography variant="body2" color="text.secondary">Loading the restart schedule...</Typography>
      </Paper>
    );
  }
  const note = (text) => (
    <Paper variant="outlined" sx={boxSx}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Restart schedule</Typography>
      <Typography variant="body2" color="text.secondary">{text}</Typography>
    </Paper>
  );
  if (reboot.state === 'unscheduled') return note('The reboot watch is not scheduled for this device, so there is no restart date.');
  if (reboot.state === 'none') return note('The reboot watch has not reported for this device yet.');
  if (reboot.state === 'error') return note(`Could not load the restart schedule: ${reboot.error}`);

  const r = reboot.item?.results || {};
  const info = r.reboot || {};
  const now = Math.floor(Date.now() / 1000);
  const rows = [];

  rows.push(['Status', REBOOT_STATUS_TEXT[r.status] || orDash(r.status)]);
  if (info.firstSeenUnix) {
    rows.push(['Restart pending since', `${formatUnix(info.firstSeenUnix)} (${formatDuration(now - info.firstSeenUnix)} ago)`]);
  }
  if (info.deadlineUnix) {
    const left = info.deadlineUnix - now;
    rows.push(['Restart scheduled for', left > 0
      ? `${formatUnix(info.deadlineUnix)} (in ${formatDuration(left)})`
      : `${formatUnix(info.deadlineUnix)} (${formatDuration(left)} overdue; restarts on its next check)`]);
  }
  if (r.status === 'restarting') {
    const why = r.reason === 'nobody-logged-on' ? 'nobody is signed in, so it restarts right away' : r.reason;
    rows.push(['Restarting', `${orDash(why)}${r.delaySeconds ? ` (in ${r.delaySeconds} seconds)` : ''}`]);
  }
  if (info.phase || r.phase) rows.push(['Reminder stage', REBOOT_PHASES[info.phase || r.phase] || (info.phase || r.phase)]);
  if (info.lastShownUnix) {
    rows.push(['Last warning shown to a user', `${formatUnix(info.lastShownUnix)} (${formatDuration(now - info.lastShownUnix)} ago)`]);
  } else if (info.firstSeenUnix) {
    rows.push(['Last warning shown to a user', 'Not shown to a user yet']);
  }
  if (info.nextNotificationUnix) rows.push(['Next reminder', formatUnix(info.nextNotificationUnix)]);
  if (info.catchUpGranted) rows.push(['Extra time', 'Given, because no one saw the final warning']);
  if (Array.isArray(info.reasons) && info.reasons.length > 0) {
    rows.push(['Why a restart is needed', info.reasons.map(x => REBOOT_REASONS[x] || x).join(', ')]);
  }

  return (
    <Paper variant="outlined" sx={boxSx}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Restart schedule</Typography>
      {rows.map(([label, value]) => (
        <Stack key={label} direction="row" spacing={1} sx={{ py: 0.25 }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>{label}</Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
        </Stack>
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        As of the reboot watch's last check, {formatTime(reboot.item?.updatedUtc)}.
      </Typography>
    </Paper>
  );
}

// Everything the Windows updates summary script reports for one device.
export function UpdateDetail({ data }) {
  const r = data || {};
  const asList = (v) => (Array.isArray(v) ? v : []);
  const byDateDesc = (a, b) => String(b.date || '').localeCompare(String(a.date || ''));
  const attention = asList(r.attention);
  const notes = asList(r.dataNotes);
  const windowsUpdates = asList(r.windowsUpdates).slice().sort(byDateDesc);
  const applications = asList(r.applicationsUpdated).slice().sort(byDateDesc);
  const failedErrors = asList(r.failedErrors);
  // A failed update carries its error code and what it usually means; the columns appear only when some update has one.
  const errorColumns = (rows) => (rows.some(row => row.error)
    ? [{ key: 'error', label: 'Error' }, { key: 'hint', label: 'What it usually means' }]
    : []);
  const s = r.summary || {};

  return (
    <Stack spacing={2}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            label={orDash(r.status)}
            color={STATUS_COLORS[String(r.status || '').toLowerCase()] || 'default'}
          />
          <Typography variant="body2" color="text.secondary">{r.statusText}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Report {orDash(r.reportDate)}
          {r.period ? ` - ${r.period.from} to ${r.period.to} (${r.period.days} days)` : ''}
        </Typography>
      </Box>

      {attention.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'error.main' }}>
          <Typography variant="subtitle2" color="error" sx={{ fontWeight: 700, mb: 0.5 }}>
            Needs attention ({attention.length})
          </Typography>
          {attention.map((a, i) => <Typography key={i} variant="body2">- {String(a)}</Typography>)}
        </Paper>
      )}

      {failedErrors.length > 0 && (
        <ListTable
          title="Failed updates by error"
          rows={failedErrors}
          columns={[
            { key: 'code', label: 'Error' }, { key: 'count', label: 'Updates' }, { key: 'hint', label: 'What it usually means' },
          ]}
        />
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <InfoBlock title="Operating system" rows={[
            ['Name', r.os?.name], ['Release', r.os?.release], ['Build', r.os?.build],
            ['Last restart', r.os?.lastRestart], ['Days since restart', r.os?.daysSinceRestart],
            ['Restart required', yesNo(r.restartRequired)],
          ]} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <InfoBlock title="Security" rows={[
            ['Last security update', r.lastSecurityUpdate?.date],
            ['KB', r.lastSecurityUpdate?.kb],
            ['Title', r.lastSecurityUpdate?.title],
            ['Days since', r.daysSinceSecurityUpdate],
            ['Defender definitions', r.defender?.definitionVersion],
            ['Defender updated', r.defender?.lastUpdated],
            ['Defender days old', r.defender?.daysOld],
          ]} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <InfoBlock title="Updates in the period" rows={[
            ['Total', s.totalUpdates], ['Windows', s.windowsUpdates], ['Security', s.securityUpdates],
            ['Driver', s.driverUpdates], ['Application', s.applicationUpdates],
            ['Defender', s.defenderUpdates], ['Failed', s.failed], ['Waiting for restart', s.waitingForRestart],
          ]} />
        </Grid>
      </Grid>

      <ListTable
        title="Windows updates"
        rows={windowsUpdates}
        columns={[
          { key: 'date', label: 'Date' }, { key: 'kb', label: 'KB' }, { key: 'title', label: 'Title' },
          { key: 'type', label: 'Type' }, { key: 'status', label: 'Status' },
          ...errorColumns(windowsUpdates),
        ]}
      />
      <ListTable
        title="Applications updated"
        rows={applications}
        columns={[
          { key: 'date', label: 'Date' }, { key: 'name', label: 'Name' },
          { key: 'version', label: 'Version' }, { key: 'status', label: 'Status' },
          ...errorColumns(applications),
        ]}
      />

      {(notes.length > 0 || r.dataComplete === false) && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Data notes{r.dataComplete === false ? ' (data incomplete)' : ''}
          </Typography>
          {notes.map((n, i) => <Typography key={i} variant="body2">- {String(n)}</Typography>)}
        </Paper>
      )}

      <Accordion variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">Raw result (JSON)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(r, null, 2)}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
